import { useState } from "react";
import { AlertTriangle, X } from "lucide-react";

// Two-step branded confirm dialog. Requires TWO explicit confirmations before firing onConfirm.
// Usage:
//   const [openFor, setOpenFor] = useState(null);
//   <ConfirmDeleteDialog open={!!openFor} title="Delete Player" itemLabel={openFor?.name}
//     firstMessage="This will permanently delete ..." secondMessage="Really really sure? ..."
//     onConfirm={async () => { ... }} onClose={() => setOpenFor(null)} />

export const ConfirmDeleteDialog = ({
  open,
  onClose,
  onConfirm,
  title = "Confirm Delete",
  itemLabel = "",
  firstMessage = "This action cannot be undone.",
  secondMessage = "Are you absolutely sure? This is the final confirmation.",
  confirmWord,
  testid = "confirm-delete",
}) => {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [typed, setTyped] = useState("");

  if (!open) return null;

  const close = () => {
    if (busy) return;
    setStep(1);
    setTyped("");
    onClose();
  };

  const firstOk = () => setStep(2);

  const finalOk = async () => {
    if (confirmWord && typed.trim().toUpperCase() !== confirmWord.toUpperCase()) return;
    setBusy(true);
    try {
      await onConfirm();
      setStep(1);
      setTyped("");
    } catch {
      // caller already surfaced the error (toast); keep dialog open
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center px-4" onClick={close} data-testid={testid}>
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0A0A0A] border border-red-400/40 p-6 sm:p-8 w-full max-w-md">
        <div className="flex justify-between items-start mb-4">
          <div className="flex items-center gap-3">
            <AlertTriangle size={22} className="text-red-400 shrink-0" />
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-red-400 font-semibold">
                {step === 1 ? "Step 1 of 2" : "Step 2 of 2 — Final Check"}
              </p>
              <h2 className="font-heading text-lg font-black mt-0.5">{title}</h2>
            </div>
          </div>
          <button onClick={close} className="text-white/50 hover:text-white" data-testid={`${testid}-close`}><X size={18} /></button>
        </div>
        {itemLabel && (
          <p className="text-[#D4AF37] text-sm font-semibold mb-3 border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-2 rounded-sm">
            {itemLabel}
          </p>
        )}
        <p className="text-white/70 text-sm leading-relaxed mb-5" data-testid={`${testid}-message`}>
          {step === 1 ? firstMessage : secondMessage}
        </p>
        {step === 2 && confirmWord && (
          <div className="mb-5">
            <label className="block text-[10px] uppercase tracking-widest text-white/40 mb-2">
              Type <span className="text-red-400 font-bold">{confirmWord}</span> to enable delete
            </label>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              className="w-full bg-[#050505] border border-white/15 text-white text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:border-red-400"
              data-testid={`${testid}-typed`}
              autoFocus
            />
          </div>
        )}
        <div className="flex gap-3 justify-end">
          <button onClick={close} disabled={busy}
            className="border border-white/20 text-white/70 text-xs uppercase tracking-widest px-5 py-2.5 rounded-sm hover:border-[#D4AF37] disabled:opacity-50"
            data-testid={`${testid}-cancel`}>
            Cancel
          </button>
          {step === 1 ? (
            <button onClick={firstOk}
              className="bg-red-500/20 border border-red-400 text-red-300 font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-red-500 hover:text-white transition-colors duration-200"
              data-testid={`${testid}-first`}>
              Continue
            </button>
          ) : (
            <button onClick={finalOk} disabled={busy || (confirmWord && typed.trim().toUpperCase() !== confirmWord.toUpperCase())}
              className="bg-red-500 text-white font-bold uppercase tracking-widest text-xs px-5 py-2.5 rounded-sm hover:bg-red-600 transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`${testid}-final`}>
              {busy ? "Deleting..." : "Yes, Delete Permanently"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
